import { Checkbox, ColumnDefinition, Flex, Table, TextField } from '@approvalmax/ui/src/components';
import { constants } from 'modules/common';
import { domain } from 'modules/data';

import { AccountCell, AmountCell, BillManualMatchingCell, ClassCell, CustomerCell, TaxCodeCell } from './components';
import { fieldMinHeight, qa } from './QBooksAccountLineItemsSection.constants';
import { messages } from './QBooksAccountLineItemsSection.messages';
import { Line, QBooksAccountLineItemsSectionProps } from './QBooksAccountLineItemsSection.types';

export const getColumnDefinitions = (params: {
    isOcrLoading: boolean;
    withTaxCode: boolean;
    withBillable: boolean;
    withTaxable: boolean;
    withCustomer: boolean;
    withClass: boolean;
    withLinked: boolean;
    taxCodes: domain.QBooksTaxCode[];
    onRemoveMatching: QBooksAccountLineItemsSectionProps['onRemoveMatching'];
    currency: string;
}) => {
    const {
        isOcrLoading,
        withTaxCode,
        withBillable,
        withTaxable,
        withCustomer,
        withClass,
        withLinked,
        taxCodes,
        onRemoveMatching,
        currency,
    } = params;

    const columnsDefinition: ColumnDefinition<Line>[] = [];

    columnsDefinition.push({
        id: 'account',
        name: messages.colNameAccount,
        width: 350,
        verticalAlign: 'top',
        cellComponent: (props) => (
            <AccountCell
                name='account'
                cellProps={props}
                placeholder={messages.cellPlaceholderTaxCode}
                disabled={isOcrLoading}
                qa={qa('cell-editor')}
            />
        ),
    });

    columnsDefinition.push({
        id: 'description',
        name: messages.colNameDescription,
        verticalAlign: 'top',
        cellComponent: (props) => (
            <Table.FormCell name={`lines.${props.item.index}.description`} focus {...props}>
                {(fieldProps) => (
                    <TextField
                        {...fieldProps}
                        placeholder={messages.cellPlaceholderDescription}
                        bordered={false}
                        size='small'
                        maxLength={constants.qBooksConstants.LINE_ITEM_DESCRIPTION_MAX_LENGTH}
                        multiline
                        autoHeight
                        changeOnBlur
                        disabled={isOcrLoading}
                        qa={qa('cell-editor')}
                    />
                )}
            </Table.FormCell>
        ),
    });

    if (withTaxCode) {
        columnsDefinition.push({
            id: 'taxCode',
            name: messages.colNameTaxCode,
            width: 250,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <TaxCodeCell
                    name='taxCode'
                    cellProps={props}
                    placeholder={messages.cellPlaceholderTaxCode}
                    items={taxCodes}
                    disabled={isOcrLoading}
                    qa={qa('cell-editor')}
                />
            ),
        });
    }

    if (withBillable) {
        columnsDefinition.push({
            id: 'isBillable',
            name: messages.colNameIsBillable,
            width: 100,
            textAlign: 'center',
            verticalAlign: 'top',
            cellComponent: (props) => (
                <Table.FormCell {...props} name={`lines.${props.item.index}.isBillable`}>
                    {(fieldProps) => (
                        <Flex alignItems='center' justifyContent='center' minHeight={fieldMinHeight}>
                            <Checkbox
                                {...fieldProps}
                                size='small'
                                disabled={isOcrLoading}
                                data-qa={qa('billable-checkbox')}
                            />
                        </Flex>
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (withTaxable) {
        columnsDefinition.push({
            id: 'isTaxable',
            name: messages.colNameIsTaxable,
            width: 100,
            textAlign: 'center',
            verticalAlign: 'top',
            cellComponent: (props) => (
                <Table.FormCell {...props} name={`lines.${props.item.index}.isTaxable`}>
                    {(fieldProps) => (
                        <Flex alignItems='center' justifyContent='center' minHeight={fieldMinHeight}>
                            <Checkbox
                                {...fieldProps}
                                size='small'
                                disabled={isOcrLoading}
                                data-qa={qa('taxable-checkbox')}
                            />
                        </Flex>
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (withCustomer) {
        columnsDefinition.push({
            id: 'customer',
            name: messages.colNameCustomer,
            width: 350,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <CustomerCell
                    name='customer'
                    cellProps={props}
                    placeholder={messages.cellPlaceholderCustomer}
                    disabled={isOcrLoading}
                    qa={qa('cell-editor')}
                />
            ),
        });
    }

    if (withClass) {
        columnsDefinition.push({
            id: 'class',
            name: messages.colNameCustomer,
            width: 100,
            verticalAlign: 'top',
            cellComponent: (props) => (
                <ClassCell
                    name='class'
                    cellProps={props}
                    placeholder={messages.cellPlaceholderClass}
                    disabled={isOcrLoading}
                />
            ),
        });
    }

    columnsDefinition.push({
        id: 'amount',
        name: messages.colNameAmount,
        width: 250,
        verticalAlign: 'top',
        textAlign: 'right',
        headerTextAlign: 'right',
        cellComponent: (props) => (
            <AmountCell
                cellProps={props}
                name='amount'
                placeholder={messages.cellPlaceholderAmount}
                disabled={isOcrLoading}
                qa={qa('cell-editor')}
                currency={currency}
                clearable={false}
            />
        ),
    });

    if (withLinked) {
        columnsDefinition.push({
            id: 'linked',
            name: messages.colNameLinked,
            width: 100,
            textAlign: 'center',
            verticalAlign: 'top',
            cellComponent: (props) => (
                <BillManualMatchingCell {...props} onRemoveMatching={onRemoveMatching} disabled={isOcrLoading} />
            ),
        });
    }

    return columnsDefinition;
};
